import { cloneDeep, isNil, isEmpty, forEach, isNumber } from 'lodash';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { ApiService } from '@nrwl/common-lib';
import { tabConfig, outputParams, stateControl } from './types';
import { BaseSetupService } from './base-setup.service';

export const REMOVED = '!REMOVED!';

export class BaseSetupTab {
  defaultId: string;
  config: tabConfig;
  //eslint-disable-next-line
  parent: any;
  setupService: BaseSetupService;
  id: string;
  defaultParamsById: outputParams;
  form: NgForm;
  isLoading = false;
  output: outputParams;
  stateOfExtendedControls: stateControl;
  debugEnabled = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  //eslint-disable-next-line
  constructor(readonly apiService: ApiService, config: tabConfig, parent: any) {
    this.defaultId = 'setupTab';
    this.form = null;
    this.config = config;
    this.parent = parent;
    this.setupService = this.findSetupService(this.parent);

    this.id = config.id;
    this.defaultParamsById = {};

    this.isLoading = false;

    this.output = this._generateOutputs();

    // stores data, submitted via dataValueChange, metaDataValueChange methods
    // used for isValid() and page reset purposes
    this.stateOfExtendedControls = {};
    this.updateDataValueChange(this.output);
  }

  //----------------------------------------- Outputs -----------------------------------------------------------

  _generateOutputs(config = this.config): outputParams {
    const output: outputParams = {};
    const allOutputs = config.def.outputs.concat(config.def?.outputOptional);
    Object.values(allOutputs).forEach(key => {
      //store default value and defaultParams in output based on mode
      const defaultValue = this.parent.editMode
        ? Object.assign({}, this.config.valuesDefault, this.parent.defaultParamsById)
        : this.config.valuesDefault;
      const params = defaultValue;
      const value = this._getOutputDefaultItem(key, params);
      output[key] = value;
    });
    return output;
  }

  isValid(): boolean {
    const componentId = this.config.def?.template;
    const isValidStandardControls =
      (isEmpty(this.form) || this.form.valid) &&
      !this.config.def.outputs.find(key => {
        const isInValidKey =
          this._getIndex(key, this.config.def.outputOptional) === -1 &&
          this.getVolume(this.output[key]);
        if (isInValidKey && this.debugEnabled) {
          console.log('Invalid field for current tab Key:- ' + key + ' Value:-' + this.output[key]);
        }
        return isInValidKey;
      });
    let isValidExtendedControls = true;
    forEach(this.stateOfExtendedControls, (value, key) => {
      const keyId = key.split('/')[0];
      if (componentId === keyId) {
        const invalidState = !value.isValid;
        if (invalidState && this.debugEnabled) {
          console.log('invalidState:- ', value);
        }
        isValidExtendedControls = isValidExtendedControls && value.isValid;
      }
    });

    return isValidStandardControls && isValidExtendedControls;
  }

  _getIndex(item: string, array = []): number {
    return array.indexOf(item);
  }

  getVolume(item: any): boolean {
    if (isNumber(item) || typeof item === 'boolean') {
      return false;
    } else return isEmpty(item);
  }

  updateDataValueChange(dataValue: outputParams): void {
    for (const [key, value] of Object.entries(dataValue)) {
      if (!isNil(value)) {
        this.dataValueChange(
          this.buildComponentIdentifier(this.config.def.template, key),
          key,
          value,
          true
        );
      }
    }
  }

  _getOutputDefaultItem(output: string, params: outputParams): string {
    if (params && !isNil(params[output])) {
      return cloneDeep(params[output]);
    }
    return null;
  }

  tabChange(): void {
    const input = {};
    this.config.def?.inputs.forEach(key => {
      input[key] = this.parent.output[key];
    });
    this.updateDataValueChange(input);
  }

  //--------------------------------------------- Backend response -------------------------------------------------------

  _changeMetaDataValue(key: string, newValue: string): void {
    if (!this.output.metaData) {
      this.output.metaData = [];
    }
    const existingPair = this.output.metaData.find({
      key
    });
    if (existingPair) {
      existingPair.value = newValue;
    } else {
      this.output.metaData.push({
        key,
        value: newValue
      });
    }
  }

  /**
   * angular.extend does shallow copy of keys from 'source' to 'target'.
   * This is fine for most use cases in context of setup.
   * However it is bad for properties that are 'arrays' of key-value pairs.
   * Eg: target:
   * {
   *    name: 'test1',
   *    meta_data: [{key1: 'value1'}]
   * }
   * And source:
   * {
   *    name: 'test2',
   *    meta_data: [{key2: 'value2'}]
   * }
   * angular.extend will produce this output:
   * {
   *  name: 'test2', // OK
   *  meta_data: [{key2: 'value2'}] // totally NOT ok
   * }
   * Eg, meta_data array is populated with key-value pair in 'originator' and
   * then in 'destination' pages. As keys are different - we want to 'merge' arrays.
   *
   * Here is the logic to implement:
   * - enumerate keys from 'source', iterate over them
   * - if key is not 'meta_data', just copy it over from source to target
   * - if key is a 'meta_data', we trigger special function that intelligently merges entries in meta_data array
   *   by 'key' keys.
   *
   */

  //eslint-disable-next-line
  copyProperties(target: any, source: any): void {
    const keys = Object.keys(source);
    for (let j = 0, jj = keys.length; j < jj; j++) {
      const key = keys[j];
      const src = source[key];

      if (this._isMetaData(key)) {
        target[key] = this._mergeMetaData(target[key], src);
      } else if (src === REMOVED) {
        // make sure 'removed' key is destroyed in output
        delete target[key];
      } else {
        target[key] = src;
      }
    }
  }

  _isMetaData(key: string): boolean {
    return key === 'metaData';
  }

  _isArray(obj: Array<string>): boolean {
    return obj.constructor === Array;
  }

  //eslint-disable-next-line
  findSetupService(parent: any): any {
    if (parent) {
      if (parent.defaultId && parent.defaultId === 'setupService') {
        return parent;
      } else {
        return this.findSetupService(parent.parent);
      }
    } else {
      return null;
    }
  }

  //eslint-disable-next-line
  _mergeMetaData(src1 = [], src2 = []): Array<any> {
    // copy over all keys from src1
    // then iterate over props from src2, search for matching entries by 'key' - and overwrite if found
    const result = [];
    if (src1 && this._isArray(src1)) {
      src1.forEach(s => {
        result.push(s);
      });
    }
    if (src2 && this._isArray(src2)) {
      src2.forEach(s => {
        if (s.value === REMOVED) {
          delete result[s.key];
        } else {
          // regular insert/update of data
          const match = result.find(data => data.key === s.key);
          if (match) {
            match.value = s.value;
          } else {
            result.push(s);
          }
        }
      });
    }
    return result;
  }

  // registers an update to value of specific component.
  // per that information, the add navigation logic will be enabled/disabled
  // also takes to update the value in final output object/with a condition that this value is
  // embedded in output object, 'meta_data' array.
  metaDataValueChange(componentId: string, key: string, newValue: string, isValid = true): void {
    this._changeMetaDataValue(key, newValue);
    this.stateOfExtendedControls[componentId] = {
      key,
      value: newValue,
      isMeta: true,
      isValid
    };
  }

  dataValueChange(componentId: string, key: string, newValue: string, isValid = true): void {
    this._changeSimpleValue(key, newValue);
    this.stateOfExtendedControls[componentId] = {
      key,
      value: newValue,
      isMeta: false,
      isValid
    };
  }

  copyOutputs(target: outputParams, source: outputParams): void {
    const keys = Object.keys(source);
    for (let j = 0, jj = keys.length; j < jj; j++) {
      const key = keys[j];
      const src = source[key];

      if (this._isMetaData(key)) {
        target[key] = this._mergeMetaData(target[key], src);
      } else if (src === REMOVED) {
        // make sure 'removed' key is destroyed in output
        delete target[key];
      } else {
        target[key] = src;
      }
    }
  }

  _changeSimpleValue(key: string, newValue: string): void {
    this.output[key] = newValue;
  }

  buildComponentIdentifier(componentId: string, keyId?: string): string {
    // create inputId based on keyId, to get the values in edit mode using stateHistory
    const inputId = this.buildInputIdentifier(keyId);
    return `${componentId}/${inputId}`;
  }

  buildInputIdentifier(inputId?: string): string {
    return `${inputId}Input`;
  }
}
